import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DragDropContext, Droppable, Draggable, DropResult, DragUpdate,
  DraggableProvidedDragHandleProps,
} from '@hello-pangea/dnd';
import {
  GripVertical, ChevronRight, CalendarClock, Edit, Trash2, CalendarDays,
  DoorOpen, DoorClosed,
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import HeatStatusChip from './HeatStatusChip';
import { derivePauses } from './heatBreaks';
import { laneTeamDragId } from './heatDragIds';
import { dropStyle } from './dnd';
import { useDelayedUnmount } from '../../hooks/useDelayedUnmount';
import { buildDayRows, dropHintKey, reorderDayHeats, PlanRow } from './heatPlanning';
import type { Heat, HeatParticipant, Category, WOD } from '../../types/heats';

interface Props {
  /** Toutes les heats de l'événement — la vue découpe elle-même les journées. */
  heats: Heat[];
  wods: WOD[];
  categories: Category[];
  heatParticipants: Record<string, HeatParticipant[]>;
  /** Nouvel ordre chronologique complet d'une journée, après un déplacement. */
  onReorderDay: (orderedHeatIds: string[]) => void;
  /** Lâchers d'ÉQUIPES (rail ↔ couloirs) — inchangés. */
  onTeamDrop: (result: DropResult) => void;
  onEdit: (heat: Heat) => void;
  onDelete: (heatId: string) => void;
  onMoveHeatToDay: (heat: Heat) => void;
  /** Ouvre l'éditeur de pause (durée à saisir). */
  onInsertBreak: (heat: Heat) => void;
  /** Retire une pause déduite : décale de `-minutes` à partir de cette heat. */
  onRemovePause: (heat: Heat, minutes: number) => void;
  /** « Déplacer vers un autre jour » sur un bloc — impossible au glisser. */
  onMoveBlockToDay: (row: PlanRow, date: string) => void;
  /** Rail des équipes à placer — doit vivre dans le MÊME DragDropContext. */
  /** Vocabulaire « course » au lieu de « WOD » sur les événements course. */
  isCourseEvent?: boolean;
  /**
   * Ouvrir / fermer une vague au choix des inscrits.
   *
   * Absent = l'événement ne fait pas choisir de créneau : aucun bouton ne
   * s'affiche. C'est le contrat qui garantit que le planning des
   * événements sans créneau reste exactement ce qu'il était.
   */
  onToggleRegistrationOpen?: (heat: Heat, ouvert: boolean) => void;
  /** Absent ⇒ pastille de statut en lecture seule. */
  onChangeStatus?: (heat: Heat, status: string) => void;
  /** Événement passé : les vagues « en cours » redeviennent modifiables. */
  eventOver?: boolean;
  sidebar?: React.ReactNode;
}

const DAY_LABEL: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' };

/**
 * Espace de travail du planning : une journée est une SUITE, pas une hiérarchie.
 *
 * Les en-têtes WOD et catégorie ne sont pas des contenants mais des étiquettes
 * posées sur des suites contiguës (voir `heatPlanning.ts`). Tout vit donc dans
 * UNE seule zone de dépôt par journée, où les trois natures de ligne — en-tête
 * de WOD, en-tête de catégorie, heat — partagent le même espace d'index. C'est
 * ce qui rend vrai « tout s'insère n'importe où » : il n'y a plus de parent à
 * respecter, seulement une position dans le temps.
 *
 * L'ancienne imbrication de zones (jour ⊃ WODs ⊃ catégories ⊃ heats) encodait
 * la hiérarchie dans le glisser-déposer lui-même, et interdisait donc par
 * construction qu'un WOD apparaisse deux fois dans une journée.
 *
 * **Glisser change QUAND, jamais QUOI.** Une heat lâchée au milieu d'un autre
 * WOD garde son WOD et sa catégorie : elle coupe le bloc d'accueil en deux et
 * porte son propre en-tête au rendu suivant. Réaffecter par glisser changerait
 * qui court — les équipes appartiennent à une catégorie et ne suivraient pas.
 */
const HeatGroupedView: React.FC<Props> = ({
  heats, wods, categories, heatParticipants,
  onReorderDay, onTeamDrop, onEdit, onDelete, onMoveHeatToDay,
  onInsertBreak, onRemovePause, onMoveBlockToDay, isCourseEvent,
  onToggleRegistrationOpen, onChangeStatus, eventOver, sidebar,
}) => {
  const { t } = useTranslation();
  const statusLabel = (s: string) => t(`heats.statusLabels.${s}`, { defaultValue: s });

  const [activeDate, setActiveDate] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [drag, setDrag] = React.useState<{ source: number; destination: number } | null>(null);

  // Journées présentes, dans l'ordre.
  const days = React.useMemo(() => {
    const byDate = new Map<string, Heat[]>();
    heats.forEach(h => {
      const key = h.date || 'no-date';
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key)!.push(h);
    });
    return [...byDate.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, list]) => ({
        date,
        label: date === 'no-date'
          ? t('heats.dateGroup.singleDay')
          : new Date(date).toLocaleDateString('fr-FR', DAY_LABEL),
        heats: list,
      }));
  }, [heats, t]);

  // Le jour affiché suit les données : à la première charge, et si le jour
  // courant disparaît (dernière heat déplacée ailleurs).
  React.useEffect(() => {
    if (days.length === 0) return;
    if (!activeDate || !days.some(d => d.date === activeDate)) setActiveDate(days[0].date);
  }, [days, activeDate]);

  const day = days.find(d => d.date === activeDate) ?? days[0];

  // Filtres de LECTURE. Ils ne changent pas le planning, ils le réduisent à ce
  // qu'on cherche — un WOD sur un événement qui en compte huit.
  const [wodFilter, setWodFilter] = React.useState<string>('all');
  const [categoryFilter, setCategoryFilter] = React.useState<string>('all');
  const filtre = wodFilter !== 'all' || categoryFilter !== 'all';

  /**
   * Un filtre actif DÉSACTIVE le glisser-déposer.
   *
   * Réordonner produit l'ordre complet de la journée. Sur une liste amputée des
   * heats masquées, ce serait un ordre partiel : les absentes se retrouveraient
   * repoussées en fin de journée sans que personne ne l'ait demandé. Et « poser
   * cette heat ici » n'a même pas de sens quand on ne voit pas ce qu'il y a
   * entre les deux voisines affichées.
   *
   * On interdit donc le geste plutôt que d'en deviner l'intention.
   */
  const dragActif = !filtre;

  // « WOD » est du vocabulaire CrossFit : en course, on court une COURSE.
  const sansEpreuve = isCourseEvent ? t('heats.noCourse') : t('heats.noWod');

  // Options des filtres : ce que porte LA JOURNÉE affichée, pas tout
  // l'événement. Proposer un WOD absent du jour donnerait une liste vide.
  const wodsDuJour = React.useMemo(() => {
    const vus = new Map<string, string>();
    (day?.heats ?? []).forEach(h => {
      if (h.wod_id && !vus.has(h.wod_id)) {
        vus.set(h.wod_id, wods.find(w => w.id === h.wod_id)?.name || h.wod_name || sansEpreuve);
      }
    });
    return [...vus].map(([id, name]) => ({ id, name }));
  }, [day, wods, sansEpreuve]);

  const categoriesDuJour = React.useMemo(() => {
    const vus = new Map<string, string>();
    (day?.heats ?? []).forEach(h => {
      const id = h.category_group_id || h.category_id;
      if (id && !vus.has(id)) {
        vus.set(id, (h.is_multi_category && h.category_names?.length)
          ? h.category_names.join(' / ')
          : (h.category_name
            || categories.find(c => c.id === id)?.name
            || t('assignments.noCategory')));
      }
    });
    return [...vus].map(([id, name]) => ({ id, name }));
  }, [day, categories, t]);

  const heatsAffichees = React.useMemo(() => {
    if (!day) return [];
    return day.heats.filter(h =>
      (wodFilter === 'all' || h.wod_id === wodFilter) &&
      (categoryFilter === 'all'
        || h.category_id === categoryFilter
        || h.category_group_id === categoryFilter),
    );
  }, [day, wodFilter, categoryFilter]);

  const { rows, heatOrder } = React.useMemo(() => {
    if (!day) return { rows: [] as PlanRow[], heatOrder: [] as string[] };
    return buildDayRows({
      heats: heatsAffichees,
      wodName: (id, sample) =>
        wods.find(w => w.id === id)?.name || sample.wod_name || sansEpreuve,
      categoryName: (id, sample) =>
        (sample.is_multi_category && sample.category_names?.length)
          ? sample.category_names.join(' / ')
          : (sample.category_name
            || categories.find(c => c.id === id)?.name
            || t('assignments.noCategory')),
    });
  }, [day, heatsAffichees, wods, categories, t, sansEpreuve]);

  // Pauses déduites de l'espacement. On passe les BLOCS et non la journée à
  // plat : `derivePauses` distingue les écarts internes à un bloc de ceux qui
  // le franchissent, les deux n'ayant pas la même cadence (voir heatBreaks.ts).
  const pauses = React.useMemo(() => derivePauses(
    rows.filter(r => r.kind === 'category')
      .map(r => r.heatIds
        .map(id => heats.find(h => h.id === id))
        .filter((h): h is Heat => !!h)),
  ), [rows, heats]);

  // Où tracer le trait. Même règle que le lâcher (voir `dropHintKey`) : les
  // positions qui donnent le même ordre final n'en font qu'une, sinon on
  // afficherait trois traits différents pour une seule destination.
  const hintKey = React.useMemo(
    () => (drag ? dropHintKey(rows, drag.source, drag.destination) : null),
    [drag, rows],
  );

  if (!day) return null;

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });

  const handleDragUpdate = (u: DragUpdate) => {
    setDrag(u.destination && u.type === 'ROW'
      ? { source: u.source.index, destination: u.destination.index }
      : null);
  };

  const handleDragEnd = (result: DropResult) => {
    setDrag(null);
    if (result.type !== 'ROW') { onTeamDrop(result); return; }
    if (!result.destination) return;
    const next = reorderDayHeats(rows, heatOrder, result.source.index, result.destination.index);
    if (next) onReorderDay(next);
  };


  return (
    <DragDropContext onDragUpdate={handleDragUpdate} onDragEnd={handleDragEnd}>
      {/* La journée est toujours nommée, même s'il n'y en a qu'une : « quel jour
          je regarde » est une question qu'on se pose dès la première, et un
          planning sans date affichée oblige à aller la chercher ailleurs.

          Avec une seule journée, c'est un TITRE et non un onglet : pas de rôle
          ARIA, pas de survol, pas de soulignement — un onglet unique promettrait
          un choix qui n'existe pas. */}
      {days.length === 1 ? (
        <div className="flex gap-6 border-b border-border mb-4">
          <div className="pb-2 pt-1 text-sm font-semibold text-foreground">
            <span className="capitalize">{day.label}</span>
            <span className="ml-2 text-xs font-normal text-muted-foreground">{day.heats.length}</span>
          </div>
        </div>
      ) : (
        <div className="flex gap-6 border-b border-border mb-4" role="tablist">
          {days.map(d => (
            <button
              key={d.date}
              role="tab"
              aria-selected={d.date === day.date}
              onClick={() => setActiveDate(d.date)}
              className={cn(
                "relative pb-2 pt-1 text-sm font-semibold transition-colors",
                d.date === day.date ? "text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <span className="capitalize">{d.label}</span>
              <span className="ml-2 text-xs text-muted-foreground">{d.heats.length}</span>
              {d.date === day.date && (
                <span
                  className="absolute inset-x-0 -bottom-px h-0.5 rounded"
                  style={{ background: 'linear-gradient(90deg, #EF4444, #F97316 45%, #FACC15)' }}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Filtres de lecture. Ils réduisent l'affichage, jamais le planning. */}
      {(wodsDuJour.length > 1 || categoriesDuJour.length > 1) && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {wodsDuJour.length > 1 && (
            <Select value={wodFilter} onValueChange={setWodFilter}>
              <SelectTrigger className="h-8 w-auto min-w-[10rem] text-xs">
                <SelectValue placeholder={t('heats.filters.allWods')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('heats.filters.allWods')}</SelectItem>
                {wodsDuJour.map(w => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {categoriesDuJour.length > 1 && (
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-8 w-auto min-w-[10rem] text-xs">
                <SelectValue placeholder={t('heats.filters.allCategories')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('heats.filters.allCategories')}</SelectItem>
                {categoriesDuJour.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {filtre && (
            <>
              <button
                onClick={() => { setWodFilter('all'); setCategoryFilter('all'); }}
                className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                {t('heats.filters.clear')}
              </button>
              {/* Dire POURQUOI le glisser ne répond plus : sans cette phrase,
                  l'organisateur croit à une panne. */}
              <span className="text-xs text-muted-foreground">
                · {t('heats.filters.dragDisabled', {
                  shown: heatsAffichees.length, total: day.heats.length,
                })}
              </span>
            </>
          )}
        </div>
      )}

      <div className="flex gap-4 flex-col md:flex-row items-start">
        {/* Rail pleine hauteur d'écran : il reste visible et offre une cible de
            dépôt large sur toute la gauche, quel que soit l'endroit du planning
            où l'on se trouve. Le suivre jusqu'au bas de la dernière heat
            donnerait un rail de plusieurs milliers de pixels dont on ne verrait
            qu'un fragment — l'inverse du but. */}
        {sidebar && (
          <div className="w-full md:w-1/3 md:sticky md:top-4 md:flex md:h-[calc(100vh-7rem)] md:flex-col">
            {sidebar}
          </div>
        )}

        <div className="flex-1 min-w-0" data-tour="heats-grouped">
          <Droppable droppableId={`day::${day.date}`} type="ROW">
            {(dayDrop) => (
              <div ref={dayDrop.innerRef} {...dayDrop.droppableProps}>
                {rows.map((row, index) => (
                  <Draggable key={row.key} draggableId={row.key} index={index} isDragDisabled={!dragActif}>
                    {(drag, snap) => (
                      <div
                        ref={drag.innerRef}
                        {...drag.draggableProps}
                        style={dropStyle(drag.draggableProps.style, snap)}
                        className={cn(
                          "relative",
                          // Même grammaire de « je soulève » que le rail : une plaque qui
                          // se détache, un liseré blanc — pas une couleur hors palette.
                          snap.isDragging && "rounded bg-card rim shadow-lg ring-1 ring-white/40",
                        )}
                      >
                        {/* Trait d'insertion en ABSOLU : il ne prend aucune place,
                            donc il ne déplace rien pendant le glissement. */}
                        {hintKey === row.key && (
                          <span
                            className="pointer-events-none absolute inset-x-0 -top-px z-20 h-0.5 rounded bg-white/70"
                            aria-hidden
                          />
                        )}
                        <div>
                          {row.kind === 'wod' && (
                            <div className="flex items-center gap-2 border-b border-border py-2">
                              <div {...drag.dragHandleProps} className="cursor-grab text-muted-foreground hover:text-foreground">
                                <GripVertical size={15} />
                              </div>
                              <span className="text-sm font-semibold">{row.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {t('heats.blockSize', { count: row.blockSize })}
                              </span>
                              <button
                                onClick={() => onMoveBlockToDay(row, day.date)}
                                title={t('heats.actions.moveToDay')}
                                aria-label={t('heats.actions.moveToDay')}
                                className="ml-auto text-muted-foreground/50 hover:text-foreground"
                              >
                                <CalendarDays size={14} />
                              </button>
                            </div>
                          )}

                          {row.kind === 'category' && (
                            <div className="flex items-center gap-2 pl-6 pt-2 pb-1">
                              <div {...drag.dragHandleProps} className="cursor-grab text-muted-foreground hover:text-foreground">
                                <GripVertical size={12} />
                              </div>
                              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                {row.label}
                              </span>
                              {row.isMultiCategory && (
                                <Badge variant="outline" className="text-[10px] py-0">Multi</Badge>
                              )}
                              <button
                                onClick={() => onMoveBlockToDay(row, day.date)}
                                title={t('heats.actions.moveToDay')}
                                aria-label={t('heats.actions.moveToDay')}
                                className="ml-auto mr-2 text-muted-foreground/40 hover:text-foreground"
                              >
                                <CalendarDays size={13} />
                              </button>
                            </div>
                          )}

                          {row.kind === 'heat' && row.heat && (
                            <HeatRow
                              heat={row.heat}
                              participants={heatParticipants[row.heat.id] || []}
                              pause={pauses[row.heat.id]}
                              isOpen={expanded.has(row.heat.id)}
                              isDragging={snap.isDragging}
                              dragHandleProps={drag.dragHandleProps}
                              onToggle={() => toggle(row.heat!.id)}
                              onEdit={onEdit}
                              onDelete={onDelete}
                              onMoveToDay={onMoveHeatToDay}
                              onInsertBreak={onInsertBreak}
                              onRemovePause={onRemovePause}
                              onToggleRegistrationOpen={onToggleRegistrationOpen}
                              onChangeStatus={onChangeStatus}
                              eventOver={eventOver}
                              statusLabel={statusLabel}
                              t={t}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {/* Fin de journée : le seul trait qui peut occuper de la place,
                    puisqu'il n'a rien en dessous à décaler. */}
                {hintKey === '__end__' && (
                  <div className="h-0.5 rounded bg-white/70" aria-hidden />
                )}
                {dayDrop.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </div>
    </DragDropContext>
  );
};

// ---------------------------------------------------------------------------

interface HeatRowProps {
  heat: Heat;
  onToggleRegistrationOpen?: (heat: Heat, ouvert: boolean) => void;
  /** Absent ⇒ pastille de statut en lecture seule. */
  onChangeStatus?: (heat: Heat, status: string) => void;
  eventOver?: boolean;
  participants: HeatParticipant[];
  pause?: number;
  isOpen: boolean;
  isDragging: boolean;
  dragHandleProps: DraggableProvidedDragHandleProps | null | undefined;
  onToggle: () => void;
  onEdit: (heat: Heat) => void;
  onDelete: (heatId: string) => void;
  onMoveToDay: (heat: Heat) => void;
  onInsertBreak: (heat: Heat) => void;
  onRemovePause: (heat: Heat, minutes: number) => void;
  statusLabel: (s: string) => string;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const HeatRow: React.FC<HeatRowProps> = ({
  heat, participants, pause, isOpen, isDragging, dragHandleProps,
  onToggle, onEdit, onDelete, onMoveToDay, onInsertBreak, onRemovePause, statusLabel, t,
  onToggleRegistrationOpen, onChangeStatus, eventOver,
}) => {
  const used = participants.length;
  // Les couloirs restent montés le temps de l'animation de fermeture (200 ms),
  // puis disparaissent : une heat repliée ne porte pas ses Draggables.
  const shouldRenderLanes = useDelayedUnmount(isOpen, 200);
  // La CAPACITÉ est celle de la heat, et rien d'autre : c'est elle qu'annonce
  // le compteur. L'étendre parce qu'une équipe déborde afficherait « 3/4 » sur
  // une heat à 3 couloirs, donc masquerait le problème qu'il faut voir.
  const capacity = heat.max_participants || 0;
  // La GRILLE, elle, va jusqu'à l'équipe la plus haute : une équipe placée
  // au-delà doit rester visible pour pouvoir être déplacée.
  const lanes = Math.max(capacity, used, ...participants.map(p => p.lane_number || 0));
  const overflow = lanes > capacity && capacity > 0;
  // Miroir exact de `_allocate_lane()` côté serveur : le premier trou, dans la
  // limite de la capacité. `undefined` = heat pleine, rien à surligner.
  const taken = new Set(participants.map(p => p.lane_number));
  const nextFreeLane = Array.from({ length: capacity || lanes }, (_, i) => i + 1)
    .find(n => !taken.has(n));

  return (
    <>
      {/* Pause DÉDUITE de l'écart : rien n'est stocké, « Retirer » resserre
          simplement le planning. */}
      {pause && !isDragging && (
        <div className="flex items-center gap-3 pl-6 py-1.5 text-xs text-muted-foreground italic">
          <span className="w-6 border-t border-dashed border-muted-foreground/40" />
          <span>{t('heats.insertBreak.derived', { count: pause })}</span>
          <button
            onClick={() => onRemovePause(heat, pause)}
            className="ml-auto not-italic hover:text-destructive"
          >
            {t('heats.insertBreak.remove')}
          </button>
        </div>
      )}

      {/* Cliquable sur toute sa largeur : ouvrir les couloirs est le geste le
          plus fréquent, il ne doit pas viser un chevron de 16 px. */}
      <div
        onClick={onToggle}
        className="flex cursor-pointer items-center gap-3 border-b border-border py-2.5 pl-6 hover:bg-muted/30 active:bg-muted/50"
      >
        <div
          {...dragHandleProps}
          onClick={e => e.stopPropagation()}
          className="cursor-grab text-muted-foreground hover:text-foreground"
        >
          <GripVertical size={13} />
        </div>
        <span className="w-6 text-right font-semibold tabular-nums text-muted-foreground">
          {heat.heat_number}
        </span>
        <span className="w-12 font-semibold tabular-nums">{heat.start_time?.substring(0, 5)}</span>
        {heat.status && (
          <HeatStatusChip
            heat={heat}
            statusLabel={statusLabel}
            onChangeStatus={onChangeStatus}
            eventOver={eventOver}
            t={t}
          />
        )}

        {/* Créneau : la vague est-elle proposée au choix des inscrits ?
            Une vague non publiée ne peut pas l'être — l'athlète choisirait un
            horaire que le planning public ne montre pas. */}
        {onToggleRegistrationOpen && (() => {
          const publiee = (heat.status || '').toLowerCase() === 'ready';
          const ouverte = !!heat.slot_open;
          return (
            <button
              onClick={e => {
                e.stopPropagation();
                if (publiee) onToggleRegistrationOpen(heat, !ouverte);
              }}
              disabled={!publiee}
              className={cn(
                "rounded p-0.5 transition-colors",
                !publiee
                  ? "cursor-not-allowed text-muted-foreground/30"
                  : ouverte
                    ? "text-success hover:text-success/80"
                    : "text-muted-foreground hover:text-foreground",
              )}
              title={
                !publiee
                  ? t('heats.slotNeedsPublish')
                  : ouverte ? t('heats.slotOpenOn') : t('heats.slotOpenOff')
              }
              aria-label={t('heats.slotToggle')}
              aria-pressed={ouverte}
            >
              {ouverte ? <DoorOpen size={14} /> : <DoorClosed size={14} />}
            </button>
          );
        })()}

        {/* Remplissage : encodé en barre ET en chiffre, pour se lire sans compter. */}
        <span className="ml-auto flex items-center gap-2">
          <span className="h-1 w-14 overflow-hidden rounded bg-muted">
            <span
              className={cn(
                "block h-full",
                overflow ? "bg-warning" : used >= capacity ? "bg-success" : "bg-muted-foreground",
              )}
              style={{ width: `${capacity ? Math.min(100, (used / capacity) * 100) : 0}%` }}
            />
          </span>
          <span
            className={cn(
              "text-xs tabular-nums",
              overflow ? "text-warning" : used >= capacity ? "text-success" : "text-muted-foreground",
            )}
            title={overflow ? t('heats.overCapacity') : undefined}
          >
            {used}/{capacity || '—'}
          </span>
        </span>

        <button
          onClick={e => { e.stopPropagation(); onToggle(); }}
          className="text-muted-foreground hover:text-foreground"
          aria-expanded={isOpen}
          aria-label={t('heats.lanes')}
        >
          <ChevronRight size={16} className={cn("transition-transform", isOpen && "rotate-90")} />
        </button>
      </div>

      {/* Déplié : les couloirs, où se fait l'assignation. L'enveloppe transitionne
          (0fr → 1fr, 200 ms), le contenu n'est monté que visible ou en fermeture. */}
      <div className={cn(
        "grid transition-[grid-template-rows] duration-200 ease-out",
        isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}>
      <div className="min-h-0 overflow-hidden">
      {shouldRenderLanes && (
        <div className="border-b border-border py-3 pl-12 pr-2">
          <div className="mb-2 text-[10px] uppercase tracking-wider text-muted-foreground">
            {t('heats.lanes')}
          </div>

          <Droppable droppableId={`heat-${heat.id}`} type="TEAM">
            {(teamDrop, teamSnap) => (
              <div
                ref={teamDrop.innerRef}
                {...teamDrop.droppableProps}
                className={cn(
                  "grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(180px,1fr))] rounded p-1",
                  teamSnap.isDraggingOver && "bg-white/[0.04]",
                )}
              >
                {Array.from({ length: lanes }, (_, i) => {
                  const occupant = participants.find(p => p.lane_number === i + 1);
                  if (!occupant) {
                    // Le couloir d'arrivée est CELUI QUE LE SERVEUR CHOISIRA —
                    // le premier libre — et non celui qu'on survole : surligner
                    // sous le curseur promettrait une place que l'équipe
                    // n'obtiendrait pas.
                    const isTarget = teamSnap.isDraggingOver
                      && !teamSnap.draggingFromThisWith
                      && i + 1 === nextFreeLane;
                    return (
                      <div
                        key={`empty-${i}`}
                        className={cn(
                          "rounded-lg border border-dashed px-3 py-2 transition-colors",
                          isTarget ? "border-solid border-white/40 bg-white/[0.08]" : "border-border",
                        )}
                      >
                        <div className={cn(
                          "text-[10px] uppercase tracking-wider",
                          isTarget ? "text-foreground" : "text-muted-foreground",
                        )}>
                          {t('heats.lane')} {i + 1}
                        </div>
                        <div className={cn(
                          "text-sm",
                          isTarget ? "text-foreground font-medium" : "text-muted-foreground/60",
                        )}>
                          {isTarget ? t('heats.laneHere') : t('heats.laneFree')}
                        </div>
                      </div>
                    );
                  }
                  // L'index dnd suit l'ordre de RENDU des seules cartes
                  // glissables — les couloirs vides n'en consomment pas, sinon
                  // la séquence aurait des trous.
                  const dndIndex = participants.filter(p => p.lane_number <= i + 1).length - 1;
                  return (
                    <Draggable
                      key={`team-${occupant.team_id}`}
                      draggableId={laneTeamDragId(heat.id, occupant.team_id)}
                      index={dndIndex}
                    >
                      {(td, ts) => (
                        <div
                          ref={td.innerRef}
                          {...td.draggableProps}
                          {...td.dragHandleProps}
                          style={dropStyle(td.draggableProps.style, ts)}
                          className={cn(
                            "cursor-grab rounded-lg border border-border px-3 py-2",
                            ts.isDragging && "border-primary bg-card rim shadow-lg scale-[1.02]",
                          )}
                        >
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                            {t('heats.lane')} {occupant.lane_number}
                          </div>
                          <div className="truncate text-sm font-medium">{occupant.team_name}</div>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {teamDrop.placeholder}
              </div>
            )}
          </Droppable>

          {/* Actions de la ligne, en toutes lettres */}
          <div className="mt-3 flex flex-wrap gap-4 text-xs">
            <button className="text-muted-foreground hover:text-foreground" onClick={() => onInsertBreak(heat)}>
              <CalendarClock size={12} className="mr-1 inline" />
              {t('heats.insertBreak.action')}
            </button>
            <button className="text-muted-foreground hover:text-foreground" onClick={() => onEdit(heat)}>
              <Edit size={12} className="mr-1 inline" />
              {t('heats.actions.modify')}
            </button>
            <button className="text-muted-foreground hover:text-foreground" onClick={() => onMoveToDay(heat)}>
              <CalendarDays size={12} className="mr-1 inline" />
              {t('heats.actions.moveToDay')}
            </button>
            <button className="text-muted-foreground hover:text-destructive" onClick={() => onDelete(heat.id)}>
              <Trash2 size={12} className="mr-1 inline" />
              {t('heats.actions.delete')}
            </button>
          </div>
        </div>
      )}
      </div>
      </div>
    </>
  );
};

export default HeatGroupedView;
